/**
 * The demo's GA4 traffic report: a pure function of the properties it is given,
 * the query and today's date. It knows nothing of connecting or importing; the
 * store in `mock.ts` owns the properties and composes this.
 */

import type { Ga4GroupBy, Ga4Property, Ga4TrafficMetrics, Ga4TrafficQuery, Ga4TrafficReport, Ga4TrafficRow } from '@/api/types'
import { ApiError } from '@/lib/api'
import { addDays, dayCount, eachDay, toDateKey } from '@/lib/filters'

import { DEMO_REFUSALS } from './mockRefusals'
import { addMetrics, dayCell, GROUPS, ZERO } from './mockTraffic'

const HISTORY_DAYS = 90
const MAX_RANGE_DAYS = 400

export interface TrafficReportInput {
  properties: readonly Ga4Property[]
  query: Ga4TrafficQuery
  /** ISO date; GA4 is imported up to the day before it. */
  today: string
  baseSessions: (property: Ga4Property) => number
}

export function buildTrafficReport({ properties, query, today, baseSessions }: TrafficReportInput): Ga4TrafficReport {
  const days = dayCount(query.from, query.to)
  if (days < 1) throw new ApiError(422, 'validation_error', DEMO_REFUSALS.fromAfterTo())
  if (days > MAX_RANGE_DAYS) throw new ApiError(422, 'validation_error', DEMO_REFUSALS.rangeTooLong(MAX_RANGE_DAYS))
  const previous = query.compare ? { from: addDays(query.from, -days), to: addDays(query.from, -1) } : null

  const current = aggregate(properties, baseSessions, today, query.groupBy, query.from, query.to)
  const before = previous ? aggregate(properties, baseSessions, today, query.groupBy, previous.from, previous.to) : null
  const keys = [...new Set([...current.keys(), ...(before?.keys() ?? [])])]
  const rows: Ga4TrafficRow[] = keys
    .map((key) => ({ key, label: key, ...(current.get(key) ?? ZERO), previous: before ? (before.get(key) ?? ZERO) : null }))
    .sort((a, b) => b.sessions - a.sessions || a.key.localeCompare(b.key))

  const sum = (pick: (row: Ga4TrafficRow) => Ga4TrafficMetrics | null) =>
    rows.reduce((total, row) => addMetrics(total, pick(row) ?? ZERO), ZERO)
  const currencies = new Set(properties.map((property) => property.currency))
  const imports = properties.flatMap((property) => (property.lastImportAt ? [property.lastImportAt] : []))
  return {
    source: 'ga4',
    start: query.from,
    end: query.to,
    groupBy: query.groupBy,
    compared: previous !== null,
    previousStart: previous?.from ?? null,
    previousEnd: previous?.to ?? null,
    currency: currencies.size === 1 ? (Array.from(currencies)[0] ?? null) : null,
    properties: properties.map((property) => ({
      ga4PropertyId: property.ga4PropertyId,
      propertyId: property.propertyId,
      displayName: property.displayName,
      currency: property.currency,
    })),
    rows,
    totals: sum((row) => row),
    totalsPrevious: previous ? sum((row) => row.previous) : null,
    freshness: { lastImportAt: imports.sort()[0] ?? null, stale: properties.some((property) => property.stale) },
  }
}

function aggregate(
  properties: readonly Ga4Property[],
  baseSessions: (property: Ga4Property) => number,
  today: string,
  groupBy: Ga4GroupBy,
  from: string,
  to: string,
): Map<string, Ga4TrafficMetrics> {
  const totals = new Map<string, Ga4TrafficMetrics>()
  for (const property of properties) {
    // GA4 is imported up to yesterday, never today, and a stale property stops where its last import did.
    const available = {
      from: addDays(today, -HISTORY_DAYS),
      to: addDays(property.lastImportAt ? toDateKey(new Date(property.lastImportAt)) : today, -1),
    }
    const start = from > available.from ? from : available.from
    const end = to < available.to ? to : available.to
    if (start > end) continue
    for (const date of eachDay({ from: start, to: end, days: dayCount(start, end) })) {
      for (const [group, weight] of GROUPS[groupBy]) {
        const cell = dayCell(property, baseSessions(property), group, weight, date)
        if (cell.sessions > 0) totals.set(group, addMetrics(totals.get(group) ?? ZERO, cell))
      }
    }
  }
  return totals
}
